import { ApiError } from '@/api/client';
import type { ApiUser } from '@/api/people';
/**
 * Ny oppgave and Endre oppgave (#293 AC02 to AC05, AC13, AC15). Only the context the manager came from is
 * filled in; validation keeps every value and focuses the first bad field; closing a changed form asks
 * keep or discard; a save another person overtook shows theirs beside the draft and saves nothing until
 * the manager chooses.
 */
import type { ApiShift } from '@/api/shifts';
import { type ApiShiftTask, useShiftTaskLibrary } from '@/api/shifts';
import { type ApiTask, type TaskCreate, useTaskWrites } from '@/api/tasks';
import { T } from '@/i18n';
import { IconPick } from '@/pages/innstillinger/IconPick';
import { ColorPick } from '@/pages/innstillinger/Pakker';
import { oslo } from '@/rules';
import { Button, Drawer } from '@/ui';
import { fullName, shiftSpan } from '@/ui/format';
import { useMemo, useRef, useState } from 'react';
import {
  type Draft,
  FIELD_ORDER,
  type Field,
  draftOf,
  hoursOf,
  occurrenceCount,
  validate,
  writeOf,
} from './model';

export interface FormOpen {
  /** the task being edited; absent for a new one */
  task?: ApiTask;
  /** what the place the manager came from knows: a person and a day, or a day */
  date?: string;
  person?: number | null;
}

const blank = (date: string, person: number | null | undefined): Draft => ({
  title: '',
  type_id: null,
  start_date: date,
  due_date: date,
  start_time: '',
  estimate: '',
  requirement: '',
  mode: person == null ? 'later' : 'person',
  assignee_id: person ?? null,
  shift_id: null,
  repeat: '',
  until: '',
  save_as_type: false,
  icon: null,
  color: null,
});

const LABEL: Record<string, string> = {
  title: 'Hva skal gjøres?',
  start_date: 'Startdato',
  due_date: 'Frist',
  start_time: 'Klokkeslett',
  estimate_minutes: 'Estimert arbeid',
  requirement: 'Dokumentasjon',
  assignee_id: 'Ansvarlig',
  shift_id: 'Vakt',
};

export function TaskForm({
  open,
  users,
  shifts,
  today,
  onClose,
  onSaved,
}: {
  open: FormOpen;
  users: ApiUser[];
  shifts: ApiShift[];
  today: string;
  onClose: () => void;
  onSaved: (task: ApiTask, created: boolean) => void;
}) {
  const editing = open.task;
  const start = useMemo(
    () => (editing ? draftOf(editing) : blank(open.date ?? today, open.person)),
    [editing, open.date, open.person, today],
  );
  const [d, setD] = useState<Draft>(start);
  const [errors, setErrors] = useState<Partial<Record<Field | 'form', string>>>({});
  const [asking, setAsking] = useState(false);
  const [scope, setScope] = useState<'one' | 'future'>('one');
  const [conflict, setConflict] = useState<ApiTask | null>(null);
  const [base, setBase] = useState(editing?.revision ?? 0);
  const [picking, setPicking] = useState(false);
  const key = useRef(`t-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const askRef = useRef<HTMLDivElement>(null);
  const w = useTaskWrites();
  const lib = useShiftTaskLibrary();
  const types = (lib.data ?? []).filter((t) => t.is_active);
  const dirty = JSON.stringify(d) !== JSON.stringify(start);
  const saving = w.create.isPending || w.update.isPending;

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setD((x) => ({ ...x, [k]: v }));
    // an error clears as soon as its field changes
    setErrors((e) => {
      const { [k as string]: _, form: __, ...rest } = e as Record<string, string>;
      return rest;
    });
  };

  // the planned shifts of the start day: person and hours, the candidates for «Fra planlagt vakt»
  const candidates = shifts.filter((s) => s.user_id != null && oslo(s.start_time)?.date === d.start_date);
  const personShift = d.assignee_id == null ? [] : candidates.filter((s) => s.user_id === d.assignee_id);
  const nameOf = (id: number | null) => {
    const u = users.find((x) => x.id === id);
    return u ? fullName(u) : '';
  };
  const hours = (s: ApiShift) => shiftSpan(s.start_time, s.end_time);

  const pickType = (t: ApiShiftTask) => {
    // a type fills title, estimate and proof; the person and the day stay as they were
    setD((x) => ({
      ...x,
      type_id: t.id,
      title: t.name,
      estimate: hoursOf(t.default_estimate_minutes ?? null),
      requirement: t.requirement ?? '',
      save_as_type: false,
    }));
    setErrors({});
    setPicking(false);
  };

  const focusField = (f: string) => requestAnimationFrame(() => document.getElementById(`opg-${f}`)?.focus());

  async function save() {
    const found = validate(d);
    const first = FIELD_ORDER.find((f) => found[f]);
    setErrors(found);
    if (first) {
      if (first === 'until') {
        const det = document.getElementById('opg-repeat-box') as HTMLDetailsElement | null;
        if (det) det.open = true;
      }
      focusField(first);
      return;
    }
    try {
      if (editing) {
        const body = {
          ...writeOf(d),
          expected_revision: base,
          ...(scope === 'future' ? { scope: 'future' as const } : {}),
        };
        const t = await w.update.mutateAsync({ id: editing.id, body });
        onSaved(t, false);
      } else {
        const body: TaskCreate = { ...writeOf(d), client_key: key.current };
        if (d.save_as_type && d.type_id == null) body.save_as_type = { icon: d.icon, color: d.color };
        if (d.repeat) body.repeat = { cadence: d.repeat, until: d.until };
        const r = await w.create.mutateAsync(body);
        onSaved(r.task, true);
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'task_changed') {
        const current = (e.body as { current?: ApiTask })?.current ?? null;
        setConflict(current);
        return;
      }
      const field = e instanceof ApiError ? (e.body as { field?: string })?.field : undefined;
      const msg = e instanceof Error ? e.message : T('Kunne ikke lagre.');
      const mapped = field === 'estimate_minutes' ? 'estimate' : field === 'repeat_until' ? 'until' : field;
      if (mapped && (FIELD_ORDER as readonly string[]).includes(mapped)) {
        setErrors({ [mapped]: msg });
        focusField(mapped);
      } else setErrors({ form: msg });
    }
  }

  const close = () => onClose();
  const blockClose = () => {
    if (!dirty || asking) return asking;
    setAsking(true);
    requestAnimationFrame(() => askRef.current?.focus());
    return true;
  };

  const err = (f: Field) =>
    errors[f] ? (
      <span className="o-err" id={`opg-${f}-err`} role="alert">
        {errors[f]}
      </span>
    ) : null;
  const inv = (f: Field) => ({
    id: `opg-${f}`,
    'aria-invalid': errors[f] ? true : undefined,
    'aria-describedby': errors[f] ? `opg-${f}-err` : undefined,
  });

  const conflictRows = conflict
    ? (Object.keys(LABEL) as (keyof typeof LABEL)[]).map((k) => {
        const mine = writeOf(d) as unknown as Record<string, unknown>;
        const theirs = conflict as unknown as Record<string, unknown>;
        const show = (v: unknown) =>
          k === 'assignee_id'
            ? nameOf(v as number | null) || T('Tildel senere')
            : v == null || v === ''
              ? '–'
              : String(v);
        return {
          k,
          label: T(LABEL[k] ?? k),
          theirs: show(theirs[k]),
          mine: show(mine[k]),
          diff: show(theirs[k]) !== show(mine[k]),
        };
      })
    : [];

  return (
    <Drawer
      open
      onClose={close}
      blockClose={blockClose}
      title={editing ? T('Endre oppgave') : T('Ny oppgave')}
      className="opgd"
      initialFocus={() => document.getElementById('opg-title')}
      footer={
        <div className="o-row">
          <Button tone="pri" onClick={save} disabled={saving || conflict != null} data-testid="opg-save">
            {saving ? T('Lagrer…') : editing ? T('Lagre endringer') : T('Opprett oppgave')}
          </Button>
          <Button onClick={() => (blockClose() ? undefined : close())}>{T('Avbryt')}</Button>
        </div>
      }
    >
      {asking && (
        <div
          className="o-decide"
          role="alertdialog"
          aria-label={T('Ulagrede endringer')}
          tabIndex={-1}
          ref={askRef}
        >
          <b>{T('Du har endringer som ikke er lagret.')}</b>
          <div className="o-row" style={{ marginTop: 8 }}>
            <Button
              small
              onClick={() => {
                setAsking(false);
                focusField('title');
              }}
            >
              {T('Fortsett å redigere')}
            </Button>
            <Button small tone="dgr" onClick={close}>
              {T('Forkast endringene')}
            </Button>
          </div>
        </div>
      )}
      {conflict && (
        <div className="o-decide" role="alert" data-testid="opg-conflict">
          <b>{T('Noen andre har endret oppgaven mens du redigerte.')}</b>
          <p className="o-hint">
            {T('Se forskjellene og velg hva som skal gjelde. Ingenting er lagret ennå.')}
          </p>
          <table className="o-cmp">
            <thead>
              <tr>
                <th>{T('Felt')}</th>
                <th>{T('Nyeste')}</th>
                <th>{T('Ditt utkast')}</th>
              </tr>
            </thead>
            <tbody>
              {conflictRows.map((r) => (
                <tr key={r.k} className={r.diff ? 'o-diff' : ''}>
                  <td>{r.label}</td>
                  <td>{r.theirs}</td>
                  <td>{r.mine}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="o-row">
            <Button
              small
              onClick={() => {
                setD(draftOf(conflict));
                setBase(conflict.revision);
                setConflict(null);
              }}
            >
              {T('Bruk nyeste')}
            </Button>
            <Button
              small
              tone="pri"
              onClick={() => {
                setBase(conflict.revision);
                setConflict(null);
              }}
            >
              {T('Behold utkastet')}
            </Button>
          </div>
        </div>
      )}
      {errors.form && (
        <p className="o-err" role="alert" style={{ marginBottom: 12 }}>
          {errors.form}
        </p>
      )}

      {!editing && (
        <div className="o-fld">
          <button
            type="button"
            className="btn ghost sm"
            aria-expanded={picking}
            onClick={() => setPicking((p) => !p)}
          >
            {T('Velg fra oppgavebiblioteket')}
          </button>
          {picking && (
            <select
              aria-label={T('Oppgavetype')}
              style={{ marginTop: 6 }}
              value=""
              onChange={(e) => {
                const t = types.find((x) => x.id === Number(e.target.value));
                if (t) pickType(t);
              }}
            >
              <option value="">{types.length ? T('Velg en type') : T('Biblioteket er tomt')}</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <label className="o-fld">
        <span>{T('Hva skal gjøres?')}</span>
        <input
          {...inv('title')}
          value={d.title}
          maxLength={200}
          placeholder={T('For eksempel: sjekk temperaturen i kjølerommet')}
          onChange={(e) => {
            set('title', e.target.value);
            if (d.type_id != null && e.target.value !== types.find((t) => t.id === d.type_id)?.name)
              set('type_id', null);
          }}
        />
        {err('title')}
        {d.type_id != null && (
          <span className="o-hint">{T('Fra oppgavetypen. Endre gjerne for denne oppgaven.')}</span>
        )}
      </label>

      <div className="o-pair">
        <label className="o-fld">
          <span>{T('Startdato')}</span>
          <input
            type="date"
            {...inv('start_date')}
            value={d.start_date}
            onChange={(e) => set('start_date', e.target.value)}
          />
          {err('start_date')}
        </label>
        <label className="o-fld">
          <span>{T('Frist')}</span>
          <input
            type="date"
            {...inv('due_date')}
            value={d.due_date}
            onChange={(e) => set('due_date', e.target.value)}
          />
          {err('due_date')}
        </label>
      </div>
      <div className="o-pair">
        <label className="o-fld">
          <span>{T('Klokkeslett på startdato')}</span>
          <input
            {...inv('start_time')}
            inputMode="numeric"
            placeholder="TT:MM"
            value={d.start_time}
            onChange={(e) => set('start_time', e.target.value)}
          />
          {err('start_time') ?? (
            <span className="o-hint">{T('Valgfritt. Tomt betyr i løpet av dagen.')}</span>
          )}
        </label>
        <label className="o-fld">
          <span>{T('Estimert arbeid (timer)')}</span>
          <input
            {...inv('estimate')}
            inputMode="decimal"
            value={d.estimate}
            onChange={(e) => set('estimate', e.target.value)}
          />
          {err('estimate') ?? (
            <span className="o-hint">{T('Planleggingsgrunnlag, ikke registrert tid.')}</span>
          )}
        </label>
      </div>

      <label className="o-fld">
        <span>{T('Tildeling')}</span>
        <select
          value={d.mode}
          onChange={(e) => {
            const mode = e.target.value as Draft['mode'];
            setD((x) => ({
              ...x,
              mode,
              shift_id: null,
              assignee_id: mode === 'later' ? null : x.assignee_id,
            }));
            setErrors({});
          }}
        >
          <option value="person">{T('Velg person')}</option>
          <option value="shift">{T('Fra planlagt vakt')}</option>
          <option value="later">{T('Tildel senere')}</option>
        </select>
      </label>
      {d.mode === 'person' && (
        <label className="o-fld">
          <span>{T('Ansvarlig')}</span>
          <select
            {...inv('assignee_id')}
            value={d.assignee_id ?? ''}
            onChange={(e) => set('assignee_id', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{T('Velg person')}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {fullName(u)}
              </option>
            ))}
          </select>
          {err('assignee_id')}
        </label>
      )}
      {d.mode === 'shift' && (
        <label className="o-fld">
          <span>{T('Planlagt vakt {date}', { date: d.start_date })}</span>
          <select
            {...inv('shift_id')}
            value={d.shift_id ?? ''}
            onChange={(e) => {
              const s = candidates.find((x) => x.id === Number(e.target.value));
              setD((x) => ({ ...x, shift_id: s?.id ?? null, assignee_id: s?.user_id ?? null }));
              setErrors({});
            }}
          >
            <option value="">
              {candidates.length ? T('Velg vakt') : T('Ingen planlagte vakter denne dagen')}
            </option>
            {candidates.map((s) => (
              <option key={s.id} value={s.id}>
                {`${nameOf(s.user_id)} · ${hours(s)}${s.shiftType?.name ? ` · ${s.shiftType.name}` : ''}`}
              </option>
            ))}
          </select>
          {err('shift_id') ??
            (candidates.length === 0 ? (
              <span className="o-hint">{T('Velg en person direkte, eller Tildel senere.')}</span>
            ) : null)}
        </label>
      )}
      {d.mode === 'person' && d.assignee_id != null && personShift.length === 0 && (
        <div className="o-box" data-testid="opg-noshift">
          <b>{T('Ingen planlagt vakt denne dagen')}</b>
          <p>
            {T(
              'Kan lagres som selvstendig oppgave. Endre dato eller person for å knytte arbeidet til en vakt.',
            )}
          </p>
          <p className="o-hint">{T('Oppgaven oppretter ingen vakt eller registrert arbeidstid.')}</p>
        </div>
      )}
      {d.mode === 'later' && (
        <p className="o-hint" style={{ marginTop: -8, marginBottom: 14 }}>
          {T('Oppgaven vises under Uten ansvarlig og i Trenger oppfølging til noen er ansvarlig.')}
        </p>
      )}

      <label className="o-fld">
        <span>{T('Dokumentasjon ved utførelse')}</span>
        <select
          value={d.requirement}
          onChange={(e) => set('requirement', e.target.value as Draft['requirement'])}
        >
          <option value="">{T('Ingen krav')}</option>
          <option value="comment">{T('Kommentar')}</option>
          <option value="measurement">{T('Måling (tall)')}</option>
        </select>
        <span className="o-hint">
          {T(
            'Kravet må oppfylles før vanlig fullføring. Lederunntak krever begrunnelse. Bilde kommer senere.',
          )}
        </span>
      </label>

      {!editing && (
        <details id="opg-repeat-box" open={Boolean(d.repeat) || Boolean(errors.until)}>
          <summary>{T('Gjentakelse')}</summary>
          <div className="o-pair" style={{ marginTop: 10 }}>
            <label className="o-fld">
              <span>{T('Hvor ofte')}</span>
              <select value={d.repeat} onChange={(e) => set('repeat', e.target.value as Draft['repeat'])}>
                <option value="">{T('Ikke gjenta')}</option>
                <option value="daily">{T('Hver dag')}</option>
                <option value="weekly">{T('Hver uke')}</option>
                <option value="fortnightly">{T('Annenhver uke')}</option>
              </select>
            </label>
            <label className="o-fld">
              <span>{T('Siste dato')}</span>
              <input
                type="date"
                {...inv('until')}
                value={d.until}
                disabled={!d.repeat}
                onChange={(e) => set('until', e.target.value)}
              />
              {err('until')}
            </label>
          </div>
          {d.repeat && occurrenceCount(d.start_date, d.repeat, d.until) > 0 && (
            <p className="o-hint" aria-live="polite">
              {T('Lager {n} oppgaver. Høyst 180 dager.', {
                n: occurrenceCount(d.start_date, d.repeat, d.until),
              })}
            </p>
          )}
          {d.repeat && d.mode === 'shift' && (
            <p className="o-err">{T('En gjentakende oppgave tildeles en person direkte eller senere.')}</p>
          )}
        </details>
      )}

      {editing?.series_id != null && (
        <fieldset className="o-fld" style={{ border: 0, padding: 0 }}>
          <span>{T('Gjelder')}</span>
          <label className="o-check">
            <input type="radio" name="opg-scope" checked={scope === 'one'} onChange={() => setScope('one')} />
            {T('Bare denne')}
          </label>
          <label className="o-check">
            <input
              type="radio"
              name="opg-scope"
              checked={scope === 'future'}
              onChange={() => setScope('future')}
            />
            {T('Denne og senere åpne i serien (utførte endres ikke)')}
          </label>
        </fieldset>
      )}

      {!editing && d.type_id == null && (
        <>
          <label className="o-check">
            <input
              type="checkbox"
              checked={d.save_as_type}
              onChange={(e) => set('save_as_type', e.target.checked)}
            />
            {T('Lagre som oppgavetype for gjenbruk')}
          </label>
          {d.save_as_type && (
            <div className="o-row" style={{ marginBottom: 14 }}>
              <IconPick task={d.title || T('Ny type')} icon={d.icon} onPick={(icon) => set('icon', icon)} />
              <ColorPick
                task={d.title || T('Ny type')}
                color={d.color}
                onPick={(color) => set('color', color)}
              />
              <span className="o-hint">
                {T('Typen lagres sammen med oppgaven, med estimat og dokumentasjon som standard.')}
              </span>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
